import { Component, OnDestroy, Input, OnInit } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { State, getAlerts } from './redux/reducer';
import { Observable, Subscription } from 'rxjs';
import { Alert } from './redux/model';
import { tap, map, filter } from 'rxjs/operators';
import { HeaderAlertService } from './header-alert.service';
import {
  trigger,
  transition,
  style,
  animate,
  query,
  stagger,
  group,
} from '@angular/animations';
import { Router, NavigationStart } from '@angular/router';

@Component({
  standalone: false,
  selector: 'header-alert',
  templateUrl: './header-alert.component.html',
  styleUrls: ['./header-alert.component.scss'],
  animations: [
    trigger('animatedList', [
      transition('* => *', [
        group([
          query(
            '.header-alert:enter',
            [
              style({ opacity: 0, height: 0, paddingTop: 0, paddingBottom: 0 }),
              stagger(300, [
                animate(500, style({ opacity: 1, height: '*', paddingTop: '*', paddingBottom: '*' })),
              ]),
            ],
            { optional: true }
          ),
          query(
            '.header-alert:leave',
            [
              style({
                opacity: 1,
                height: '*',
                paddingTop: '*',
                paddingBottom: '*',
              }),
              animate(300, style({ height: 0, paddingTop: 0, paddingBottom: 0, opacity: 0 })),
            ],
            { optional: true }
          ),
        ]),
      ]),
    ]),
  ],
})
export class HeaderAlertComponent implements OnInit, OnDestroy {
  alerts$: Observable<Alert[]>;
  loadedIds: number[];
  oneTimeIds: number[];
  timeouts: number[];
  routerSubscription: Subscription;
  // must be unique for every change in alert list
  animationState = 0;
  @Input() limit = 5;
  // top position in px
  @Input() top = 0;

  constructor(
    private store: Store<State>,
    private service: HeaderAlertService,
    private router: Router
  ) {
    this.timeouts = [];
    this.loadedIds = [];
    this.oneTimeIds = [];
  }

  ngOnInit() {
    this.alerts$ = this.store.pipe(
      select(getAlerts),
      map(alerts =>
        [...alerts] // show closable last
          .sort((a: Alert, b: Alert) => +a.closable - +b.closable)
          .slice(0, this.limit)
      ),
      tap((alerts: Alert[]) => {
        this.loadedIds = [];
        this.oneTimeIds = [];
        const now = Date.now();
        alerts.forEach(alert => {
          this.loadedIds.push(alert.id);
          if (alert.endTime) {
            this.timeouts.push(
              window.setTimeout(() => {
                this.close(alert.id);
              }, alert.endTime - now)
            );
          } else {
            this.oneTimeIds.push(alert.id);
          }
        });
        setTimeout(() => {
          this.animationState = this.loadedIds.reduce((a, b) => a + b, 0);
        }, 100);
      })
    );
    this.routerSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(_ => this.service.remove(this.oneTimeIds));
  }

  close(id: number) {
    this.service.remove([id]);
  }

  ngOnDestroy() {
    if (this.oneTimeIds.length) {
      this.service.remove(this.oneTimeIds);
    }
    this.timeouts.forEach(timeout => clearTimeout(timeout));
    this.routerSubscription.unsubscribe();
  }
}
